import { useCallback, useMemo } from 'react';
import { CardTypesEnum } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import { useIsServerError } from '@/utils/hooks/useIsServerError';

import { PlanStatuses, PlanTypesLabel } from '../../plan.types';
import { usePlanListQuery } from '../queries';

type SortedPlans = {
  category: PlanTypesLabel;
  categoryEnum: CardTypesEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plans: any[];
};

const mergePlanCategories = (base: SortedPlans[], incoming: SortedPlans[]) => {
  if (!incoming.length) return base;

  const merged = base.map((item) => ({
    ...item,
    plans: [...item.plans],
  }));

  for (const incomingItem of incoming) {
    const existing = merged.find((item) => item.category === incomingItem.category);

    if (existing) {
      existing.plans = [...existing.plans, ...incomingItem.plans.map((plan) => ({ ...plan }))];
    } else {
      merged.push({
        ...incomingItem,
        plans: incomingItem.plans.map((plan) => ({ ...plan })),
      });
    }
  }

  return merged;
};

export default (searchValue: string, type?: OrganizationTypes) => {
  const {
    data: draftPlans,
    formattedArrayPlans: listDraftPlans,
    isLoading: isDraftPlansLoading,
    error: draftError,
    refetch: draftPlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.DRAFT, searchValue);
  const {
    data: activePlans,
    formattedArrayPlans: listActivePlans,
    isLoading: isActivePlansLoading,
    error: activeError,
    refetch: activePlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.ACTIVE, searchValue);
  const {
    data: readyForSetupPlans,
    formattedArrayPlans: listReadyForSetupPlans,
    isLoading: isReadyForSetupPlansLoading,
    error: readyForSetupError,
    refetch: readyForSetupPlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.READY_FOR_SETUP, searchValue);
  const {
    data: pendingTerminationPlans,
    formattedArrayPlans: listPendingTerminationPlans,
    isLoading: isPendingTerminationPlansLoading,
    error: pendingTerminationError,
    refetch: pendingTerminationPlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.PENDING_TERMINATION, searchValue);
  const {
    data: terminatedPlans,
    formattedArrayPlans: listTerminatedPlans,
    isLoading: isTerminatedPlansLoading,
    error: terminatedError,
    refetch: terminatedPlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.TERMINATED, searchValue);
  const {
    data: terminationInProgressPlans,
    formattedArrayPlans: listTerminationInProgressPlans,
    isLoading: isTerminationInProgressPlansLoading,
    error: terminationInProgressError,
    refetch: terminationInProgressPlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.TERMINATION_IN_PROGRESS, searchValue);
  const {
    data: pendingReactivationPlans,
    formattedArrayPlans: listPendingReactivationPlans,
    isLoading: isPendingReactivationPlansLoading,
    error: pendingReactivationError,
    refetch: pendingReactivationPlansRefetch,
  } = usePlanListQuery(null, PlanStatuses.PENDING_REACTIVATION, searchValue);

  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);

  const isLoading = useMemo(
    () =>
      isDraftPlansLoading ||
      isActivePlansLoading ||
      isReadyForSetupPlansLoading ||
      isTerminatedPlansLoading ||
      isPendingTerminationPlansLoading ||
      isTerminationInProgressPlansLoading ||
      isPendingReactivationPlansLoading,
    [
      isActivePlansLoading,
      isDraftPlansLoading,
      isReadyForSetupPlansLoading,
      isPendingTerminationPlansLoading,
      isTerminatedPlansLoading,
      isTerminationInProgressPlansLoading,
      isPendingReactivationPlansLoading,
    ]
  );

  const count = useMemo(
    () =>
      isSubsidiary
        ? listActivePlans.length +
          listTerminatedPlans.length +
          listPendingTerminationPlans.length +
          listTerminationInProgressPlans.length +
          listPendingReactivationPlans.length
        : listActivePlans.length +
          listDraftPlans.length +
          listReadyForSetupPlans.length +
          listTerminatedPlans.length +
          listPendingTerminationPlans.length +
          listTerminationInProgressPlans.length +
          listPendingReactivationPlans.length,
    [
      isSubsidiary,
      listActivePlans.length,
      listDraftPlans.length,
      listReadyForSetupPlans.length,
      listPendingTerminationPlans.length,
      listTerminatedPlans.length,
      listTerminationInProgressPlans.length,
      listPendingReactivationPlans.length,
    ]
  );
  const currentError = useMemo(
    () =>
      draftError ||
      activeError ||
      readyForSetupError ||
      terminatedError ||
      pendingTerminationError ||
      terminationInProgressError ||
      pendingReactivationError,
    [
      draftError,
      activeError,
      readyForSetupError,
      pendingTerminationError,
      terminatedError,
      terminationInProgressError,
      pendingReactivationError,
    ]
  );
  const currentRefetch = useMemo(() => {
    if (draftError) return draftPlansRefetch;
    if (activeError) return activePlansRefetch;
    if (pendingTerminationError) return pendingTerminationPlansRefetch;
    if (terminatedError) return terminatedPlansRefetch;
    if (terminationInProgressError) return terminationInProgressPlansRefetch;
    if (pendingReactivationError) return pendingReactivationPlansRefetch;
    return readyForSetupPlansRefetch;
  }, [
    activeError,
    activePlansRefetch,
    draftError,
    draftPlansRefetch,
    readyForSetupPlansRefetch,
    pendingTerminationError,
    pendingTerminationPlansRefetch,
    terminatedError,
    terminatedPlansRefetch,
    terminationInProgressError,
    terminationInProgressPlansRefetch,
    pendingReactivationError,
    pendingReactivationPlansRefetch,
  ]);

  const { isServerError, setIsServerError } = useIsServerError(currentError);

  const activeAndPendingTerminationPlans = useMemo(() => {
    return mergePlanCategories(activePlans, pendingTerminationPlans);
  }, [activePlans, pendingTerminationPlans]);

  const terminatedAndInProgressPlans = useMemo(() => {
    const merged = mergePlanCategories(terminatedPlans, terminationInProgressPlans);
    return mergePlanCategories(merged, pendingReactivationPlans);
  }, [terminatedPlans, terminationInProgressPlans, pendingReactivationPlans]);

  const listActiveAndPendingTerminationPlans = useMemo(() => {
    if (!listPendingTerminationPlans?.length) {
      return listActivePlans;
    }
    return [...listActivePlans, ...listPendingTerminationPlans];
  }, [listActivePlans, listPendingTerminationPlans]);

  const listTerminatedAndInProgressPlans = useMemo(() => {
    const baseList = listTerminationInProgressPlans?.length 
      ? [...listTerminatedPlans, ...listTerminationInProgressPlans]
      : listTerminatedPlans;
    
    return listPendingReactivationPlans?.length
      ? [...baseList, ...listPendingReactivationPlans]
      : baseList;
  }, [listTerminatedPlans, listTerminationInProgressPlans, listPendingReactivationPlans]);

  const refetchTerminatedPlans = useCallback(() => {
    terminatedPlansRefetch();
    terminationInProgressPlansRefetch();
    pendingReactivationPlansRefetch();
  }, [terminatedPlansRefetch, terminationInProgressPlansRefetch, pendingReactivationPlansRefetch]);

  return {
    count,
    isLoading,
    isServerError,
    currentRefetch,
    refetchTerminatedPlans,
    setIsServerError,
    readyForSetupPlans,
    activePlans: activeAndPendingTerminationPlans,
    draftPlans,
    listActivePlans: listActiveAndPendingTerminationPlans,
    listDraftPlans,
    listReadyForSetupPlans,
    isDraftPlansLoading,
    isActivePlansLoading: isActivePlansLoading || isPendingTerminationPlansLoading,
    isReadyForSetupPlansLoading,
    listTerminatedPlans: listTerminatedAndInProgressPlans,
    terminatedPlans: terminatedAndInProgressPlans,
    isTerminatedPlansLoading: isTerminationInProgressPlansLoading || isTerminatedPlansLoading || isPendingReactivationPlansLoading,
  };
};
